import { create } from "zustand";

/**
 * What the user currently has open, published by whichever page is showing it.
 *
 * <h2>Why this exists</h2>
 * The AI Companion needs to know what the user is looking at so it can offer questions about that record
 * and resolve "summarise this". The route alone cannot say: the OSCAL authoring pages (Catalog, Profile,
 * Control Mapping, Component Definition, SSP) deliberately keep the selection in local component state and
 * leave the URL on the list path — see the comment at `Catalogs.tsx` `handleSelectCatalog`. So `useParams`
 * returns nothing while the user is reading a record.
 *
 * <h2>Why it is not part of the companion store</h2>
 * Pages publish "the record I am showing" as a fact about themselves; they should not have to import the
 * companion to do it, and nothing here is companion-specific. Keeping it neutral also leaves room for the
 * active tab or applied filters later, without pages gaining a dependency on a chat panel.
 *
 * <h2>Advisory only</h2>
 * The id is passed to the backend for correlation and cache-keying and is never dereferenced there;
 * only the name is used, to word questions. Nothing here grants access to anything — the backend filters
 * suggestions by the caller's own permissions regardless of what a page publishes.
 */
export interface SelectedRecord {
  /** Whatever the page uses to identify the record. Opaque — may be a UUID or a list index. */
  id: string;
  /** Display name, used verbatim in suggestion text. */
  name: string;
  /**
   * Display name of the record this one sits inside — the catalog a control belongs to, the definition
   * a component belongs to. Optional, and only worth publishing once a page exposes something below its
   * top-level record: "How would you explain AC-2?" is ambiguous across catalogs in a way "Which controls
   * does RMIT contain?" never was.
   */
  parentName?: string;
}

/**
 * Which part of the page the user is in.
 *
 * <h2>Why the route is not enough</h2>
 * `routes.ts` declares two catalog paths, and `Catalogs.tsx` renders the list, the catalog header, the
 * control-groups tree and one control's detail behind them. A user moving between those never changes the
 * URL, so before this the companion offered list-level questions to someone reading a single control.
 * The distinction exists only in component state, which makes this file its only possible author.
 *
 * <h2>These strings are a contract</h2>
 * The backend matches them, case-insensitively, against the `views:` blocks in its capability YAML —
 * `grc-ai-companion/src/main/resources/capability/*.yaml`. Publishing a key that has no block there is
 * safe and falls back to the screen's own questions, but it also does nothing, so a new key belongs in
 * both places. Nothing here grants access to anything: the view only ever picks wording from a pool the
 * caller was already permitted to see.
 */
export type PageView =
  | "CATALOG_LIST"
  // The type tabs on the catalog list. Four filters on one unchanging route, so without these a user
  // looking only at regulatory catalogs is offered the same questions as one looking at every catalog
  // on the platform. "All" stays on the plain key above.
  | "CATALOG_LIST_REGULATORY"
  | "CATALOG_LIST_TECHNOLOGY"
  | "CATALOG_LIST_INTEGRATION"
  | "CATALOG_DETAIL"
  // One group of a catalog's or a baseline's control tree. Shared between the two screens for the same
  // reason CONTROL_DETAIL is: a control group is the same thing to the assistant however it was reached.
  | "CONTROL_GROUP_DETAIL"
  | "CONTROL_DETAIL"
  | "PROFILE_LIST"
  | "PROFILE_DETAIL"
  | "MAPPING_LIST"
  | "MAPPING_DETAIL"
  | "COMPONENT_LIST"
  | "COMPONENT_DETAIL"
  | "COMPONENT_ITEM_DETAIL"
  | "SSP_LIST"
  | "SSP_DETAIL"
  | "SSP_COMPONENT_DETAIL"
  // Authoring surfaces. Both are full create flows on their list page's route, so without these the
  // companion offers list questions to someone half-way through building a catalog or a profile.
  | "CATALOG_CREATE"
  | "PROFILE_BUILDER"
  // Executive Insights: three whole dashboards behind one path and no URL change at all. The last two
  // are the *other* screens' content, and the backend maps them to those screens rather than to a
  // corner of this one — see `Screen.forView`.
  | "INSIGHTS_SUMMARY"
  | "INSIGHTS_METRICS"
  | "INSIGHTS_QUADRANT"
  // Content Registry and Governance: tabs on one unchanging route.
  | "DOCUMENTS"
  | "EVIDENCE"
  | "POLICIES"
  | "WORKFLOWS"
  | "DELEGATION"
  | "CHANGES";

interface UiContextStore {
  selectedItem: SelectedRecord | null;
  view: PageView | null;
  /**
   * What the UI itself calls the page on screen, when the page has a better answer than its route.
   *
   * The heading the page renders, published by the page that renders it. Some titles cannot be known
   * anywhere else: the role dashboard's is `roleDashboardTitle(userRoles)`, so it reads "Administrator
   * Dashboard" for one user and "GRC Dashboard" for another. And a copy of a heading kept anywhere but
   * beside the heading is a copy that drifts from it — which is exactly how the assistant came to
   * announce "Documents" over a tab labelled "Governance Documents".
   *
   * Prose, never resolved: it reaches the assistant as `PageContext.screen`, which selects wording and
   * nothing else. It cannot name a page the caller is not on, and it grants nothing.
   */
  pageTitle: string | null;
  /**
   * Publish (or clear, with `null`) the open record.
   *
   * A no-op when nothing changed, so pages can call it from an effect on every render without waking
   * subscribers or causing the companion to refetch suggestions in a loop.
   */
  setSelectedItem: (item: SelectedRecord | null) => void;
  /** Publish (or clear) the open view. No-op when unchanged, for the same reason. */
  setView: (view: PageView | null) => void;
  /**
   * Publish all of it at once.
   *
   * Preferred over separate calls from the same effect: they land in one update, so a subscriber cannot
   * observe the moment where the view says CONTROL_DETAIL and the record is still the catalog, and the
   * companion cannot fire a request for that half-state.
   *
   * @param title optional — omit it on a page whose route already names it, which is most of them
   */
  setPageContext: (
    view: PageView | null,
    item: SelectedRecord | null,
    title?: string | null,
  ) => void;
  /** Convenience for effect cleanup, so leaving a page cannot leak its record to the next one. */
  clearSelectedItem: () => void;
  /** Clears every field. What a page should call on unmount. */
  clearPageContext: () => void;
}

/** True when two selections describe the same record. */
function sameRecord(a: SelectedRecord | null, b: SelectedRecord | null): boolean {
  if (a === b) return true;
  return (
    !!a &&
    !!b &&
    a.id === b.id &&
    a.name === b.name &&
    (a.parentName ?? null) === (b.parentName ?? null)
  );
}

export const useUiContextStore = create<UiContextStore>((set, get) => ({
  selectedItem: null,
  view: null,
  pageTitle: null,

  setSelectedItem: (item) => {
    if (sameRecord(get().selectedItem, item)) return;
    set({ selectedItem: item });
  },

  setView: (view) => {
    if (get().view === view) return;
    set({ view });
  },

  setPageContext: (view, item, title = null) => {
    const state = get();
    if (
      state.view === view &&
      state.pageTitle === title &&
      sameRecord(state.selectedItem, item)
    ) {
      return;
    }
    set({ view, selectedItem: item, pageTitle: title });
  },

  clearSelectedItem: () => {
    if (get().selectedItem !== null) {
      set({ selectedItem: null });
    }
  },

  clearPageContext: () => {
    const state = get();
    if (state.selectedItem !== null || state.view !== null || state.pageTitle !== null) {
      set({ selectedItem: null, view: null, pageTitle: null });
    }
  },
}));
